import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Select } from 'antd';
import type { EChartsOption } from 'echarts';

import { ASSET_TYPE_MAP } from '@/shared/api/openmetadata';
import { FONT, RADIUS, SEMANTIC_COLOR, SPACE } from '@/shared/design-tokens/design-tokens';
import { formatPercentageWithFloor } from '@/shared/lib/utils';
import { AssetTypeStats } from '@/shared/ui/AssetTypeStats';
import { DonutChart } from '@/shared/ui/DonutChart';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';

import { useDataAssetsOverview } from '@/features/home-stats';

import styles from './DataAssetsOverview.module.css';

type TooltipFormatterParams = {
  color: string;
  name: string;
  value: number;
  percent: number;
};

const DONUT_CHART_SIZE = 280;

/** API가 '오늘' 행을 아직 안 줄 때(전일까지 집계만 있음) 마지막 슬롯이 비어 도넛이 비지 않도록, 기간 내 가장 최근 집계일을 씀 */
function pickLatestDayWithCounts<T extends { total: number; groups: Record<string, number> }>(
  dailyData: T[]
): T | undefined {
  if (dailyData.length === 0) return undefined;
  for (let i = dailyData.length - 1; i >= 0; i--) {
    const d = dailyData[i];
    if (d.total > 0 || Object.values(d.groups).some((c) => c > 0)) {
      return d;
    }
  }
  return dailyData[dailyData.length - 1];
}

export const DataAssetsOverview: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<7 | 14 | 30 | 90>(7);
  const { overview, cycloneEntityCounts } = useDataAssetsOverview(period);

  const handleAssetTypeClick = useCallback(
    (assetType: string) => {
      const searchFilter = ASSET_TYPE_MAP[assetType]?.searchFilter || assetType;
      navigate(`/search?assetType=${searchFilter}`);
    },
    [navigate]
  );

  const processedData = useMemo(() => {
    if (!overview.dailyData || overview.dailyData.length === 0) {
      return {
        latestData: null,
        firstData: null,
        totalAssets: 0,
      };
    }

    const latestData = pickLatestDayWithCounts(overview.dailyData);
    const firstData = overview.dailyData[0];

    return {
      latestData: latestData ?? null,
      firstData,
      totalAssets: latestData?.total ?? 0,
    };
  }, [overview]);

  const dates = useMemo(() => overview.dailyData.map((dayData) => dayData.date), [overview.dailyData]);

  const assetTypeStats = useMemo(() => {
    if (!processedData.latestData || !processedData.firstData) return [];

    const { latestData, firstData } = processedData;

    return Object.entries(latestData.groups)
      .map(([type, currentCount]) => {
        const previousCount = firstData.groups[type] || 0;
        const change = currentCount - previousCount;

        let changeRateDisplay: string;
        if (previousCount === 0) {
          changeRateDisplay = change > 0 ? '-' : '0%';
        } else {
          const changeRate = (change / previousCount) * 100;
          const formattedRate = formatPercentageWithFloor(Math.abs(changeRate));
          if (change > 0) {
            changeRateDisplay = `+${formattedRate}%`;
          } else if (change < 0) {
            changeRateDisplay = `-${formattedRate}%`;
          } else {
            changeRateDisplay = '0%';
          }
        }

        const trend = overview.dailyData.map((dayData) => dayData.groups[type] || 0);

        return {
          type,
          name: ASSET_TYPE_MAP[type]?.name || type,
          count: currentCount,
          change,
          changeRate: changeRateDisplay,
          color: ASSET_TYPE_MAP[type]?.color || SEMANTIC_COLOR.CONTAINER.COLORFUL.GRAY.LEVEL2,
          trend,
          dates,
        };
      })
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return a.name.localeCompare(b.name, 'ko-KR');
      });
  }, [processedData, overview.dailyData, dates]);

  const chartData = useMemo(() => {
    if (!processedData.latestData) return [];

    return Object.entries(processedData.latestData.groups)
      .filter(([, count]) => count > 0)
      .map(([group, count]) => ({
        name: ASSET_TYPE_MAP[group]?.name || group,
        value: count,
        itemStyle: {
          color: ASSET_TYPE_MAP[group]?.color || SEMANTIC_COLOR.CONTAINER.COLORFUL.GRAY.LEVEL4,
        },
      }));
  }, [processedData]);

  const tooltipOption = useMemo(() => {
    return {
      trigger: 'item',
      confine: true,
      backgroundColor: SEMANTIC_COLOR.SURFACE.STANDARD.OPACITY_LEVEL1,
      textStyle: {
        color: SEMANTIC_COLOR.ON_SURFACE.STANDARD.LEVEL1,
        fontSize: FONT.SIZE.SM,
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontWeight: FONT.WEIGHT.REGULAR,
      },
      borderWidth: 1,
      borderColor: SEMANTIC_COLOR.SURFACE.STANDARD.LEVEL4,
      borderRadius: RADIUS[8],
      padding: [SPACE[16]],
      extraCssText: 'backdrop-filter: blur(8px); box-shadow: none;',
      formatter: (params: TooltipFormatterParams) => {
        const color = params.color;
        const name = params.name;
        const value = params.value;
        const percent = params.percent;

        const formattedPercent = formatPercentageWithFloor(percent);

        return `
            <div style="display: flex; align-items: center; gap: ${SPACE[8]}px; width: 240px; height: ${SPACE[16]}px;">
              <div style="
                width: ${SPACE[8]}px;
                height: ${SPACE[8]}px;
                border-radius: ${RADIUS.FULL};
                background-color: ${color};
              "></div>
              <span style="
                font-family: ${FONT.FAMILY.NANUMSQUARE};
                font-size: ${FONT.SIZE.SM};
                line-height: ${FONT.LINE_HEIGHT[16]};
                font-weight: ${FONT.WEIGHT.REGULAR};
                letter-spacing: 0;
                color: ${SEMANTIC_COLOR.ON_SURFACE.STANDARD.LEVEL1};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                flex: 1;
                min-width: 0;
              ">${name}</span>
              <div style="
                display: flex;
                align-items: center;
                gap: ${SPACE[8]}px;
                margin-left: auto;
                text-align: right;
              ">
                <span style="
                  font-family: ${FONT.FAMILY.NANUMSQUARE};
                  font-size: ${FONT.SIZE.SM};
                  line-height: ${FONT.LINE_HEIGHT[16]};
                  font-weight: ${FONT.WEIGHT.REGULAR};
                  letter-spacing: 0;
                  color: ${SEMANTIC_COLOR.ON_SURFACE.STANDARD.LEVEL5};
                ">${formattedPercent}%</span>
                <span style="
                  font-family: ${FONT.FAMILY.NANUMSQUARE};
                  font-size: ${FONT.SIZE.SM};
                  line-height: ${FONT.LINE_HEIGHT[16]};
                  font-weight: ${FONT.WEIGHT.BOLD};
                  letter-spacing: 0;
                  color: ${SEMANTIC_COLOR.ON_SURFACE.STANDARD.LEVEL1};
                ">${value.toLocaleString()}</span>
              </div>
            </div>
          `;
      },
    };
  }, []);

  const handlePeriodChange = useCallback((newPeriod: 7 | 14 | 30 | 90) => {
    setPeriod(newPeriod);
  }, []);

  return (
    <div
      className={styles.container}
      data-cyclone-entity-counts={cycloneEntityCounts.results.length}
      data-openmetadata-days={overview.dailyData.length}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <div className={styles.title}>총 데이터 자산</div>
        </div>
        <div className={styles.periodSelector}>
          <Select
            value={period}
            onChange={handlePeriodChange}
            className={styles.periodSelect}
            options={[
              { value: 7, label: '최근 7일' },
              { value: 14, label: '최근 14일' },
              { value: 30, label: '최근 30일' },
              { value: 90, label: '최근 90일' },
            ]}
          />
        </div>
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.chartContainer}>
          <DonutChart
            data={chartData}
            isTotalVisible={true}
            notMerge={true}
            lazyUpdate={false}
            tooltipOption={tooltipOption as EChartsOption['tooltip']}
            style={{ height: `${DONUT_CHART_SIZE}px`, width: `${DONUT_CHART_SIZE}px` }}
          />
        </div>

        <div className={styles.statsContainer}>
          <div className={styles.statsContent}>
            <NoDataBoundary data={assetTypeStats} emptyDescription="자산 타입 통계가 없습니다">
              <AssetTypeStats stats={assetTypeStats} onAssetTypeClick={handleAssetTypeClick} />
            </NoDataBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};
